/**
 * Cœur fuseaux horaires — conversions pures en `Intl`/`Date` natif, ZÉRO
 * dépendance (le projet n'embarque volontairement aucune lib de date).
 *
 * Tout le reste du cockpit (horloge, shifts timezone-aware, programmation de
 * posts) passe par ce module. Il reste React-free : l'atome d'affichage et le
 * hook vivent dans `src/core/state/displayTimezone.ts`.
 *
 * Modèle mental :
 *   - un "wall-clock" = des nombres lus sur une horloge murale (an/mois/jour/
 *     heure/minute) DANS un fuseau donné. Ce n'est PAS un instant tant qu'on ne
 *     lui associe pas un fuseau.
 *   - un "epoch ms" = un instant absolu (UTC), indépendant du fuseau.
 *   `zonedWallTimeToUtc` transforme un wall-clock + fuseau → instant ;
 *   `formatTimeInZone`/`weekdayInZone`/… font le chemin inverse (instant → ce
 *   qu'affiche une horloge dans tel fuseau).
 *
 * DST : tout est correct vis-à-vis des changements d'heure car on lit l'offset
 * réel via `Intl.DateTimeFormat.formatToParts` à l'instant considéré.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

// ─── Résolution du fuseau d'affichage ────────────────────────────────────────

/** Fuseau IANA de l'appareil (ex. "Europe/Paris"). Fallback "UTC". */
export function deviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** true si `tz` est un fuseau IANA accepté par le runtime. Garde une valeur
 *  persistée potentiellement périmée / non supportée. */
export function isValidTimezone(tz: string | null | undefined): boolean {
  if (!tz) return false;
  try {
    // Lève RangeError si la zone est inconnue.
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Fuseau EFFECTIF d'affichage : override explicite s'il est valide, sinon
 *  celui de l'appareil. `null`/invalide ⇒ device. */
export function resolveDisplayTimezone(override: string | null): string {
  if (override && isValidTimezone(override)) return override;
  return deviceTimezone();
}

// ─── Primitive : composants & offset d'un instant dans un fuseau ──────────────

const PART_KEYS = ["year", "month", "day", "hour", "minute", "second"] as const;
type PartKey = (typeof PART_KEYS)[number];

export interface WallParts {
  year: number;
  month: number; // 1..12
  day: number; // 1..31
  hour: number; // 0..23
  minute: number; // 0..59
  second: number; // 0..59
}

const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let fmt = partsFormatterCache.get(tz);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      // `h23` plutôt que `hour12:false` : évite le "24" que certains moteurs
      // émettent à minuit.
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    partsFormatterCache.set(tz, fmt);
  }
  return fmt;
}

/** Composants du wall-clock de `epochMs` tels que LUS dans `tz`. */
export function partsInZone(epochMs: number, tz: string): WallParts {
  const parts = partsFormatter(tz).formatToParts(new Date(epochMs));
  const out: Record<PartKey, number> = {
    year: 0,
    month: 1,
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
  };
  for (const p of parts) {
    if ((PART_KEYS as readonly string[]).includes(p.type)) {
      out[p.type as PartKey] = Number(p.value);
    }
  }
  // Filet : certains moteurs rendent "24" à minuit selon le cycle.
  if (out.hour === 24) out.hour = 0;
  return out;
}

/** Reconstruit un epoch en traitant les composants COMME s'ils étaient UTC. */
function wallPartsToUtcMs(p: WallParts): number {
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** Offset UTC de `tz` à l'instant `epochMs`, en minutes (est de l'UTC = positif).
 *  offset = (wall-clock interprété comme UTC) − (instant UTC réel). */
export function zoneOffsetMinutes(epochMs: number, tz: string): number {
  const asUtc = wallPartsToUtcMs(partsInZone(epochMs, tz));
  return Math.round((asUtc - epochMs) / 60000);
}

// ─── Inverse : wall-clock-en-zone → epoch ms (le morceau délicat) ─────────────

export interface WallClock {
  year: number; // année pleine, ex. 2026
  month: number; // 1..12
  day: number; // 1..31
  hour: number; // 0..23
  minute: number; // 0..59
  second?: number;
}

/**
 * Epoch ms (UTC) d'un wall-clock interprété dans `tz`. DST-correct.
 *
 * Algorithme guess→correct→verify :
 *   1. Traiter les composants comme s'ils étaient UTC → `naiveUtc`.
 *   2. Lire l'offset de la zone à `naiveUtc` → `o1`. Première estimation de
 *      l'instant vrai = `naiveUtc - o1*60000` (une zone à +120 min voit un
 *      wall-clock donné 120 min AVANT l'instant UTC correspondant).
 *   3. L'offset à cette estimation peut différer (on a franchi une bascule DST
 *      en décalant). Relire l'offset à l'estimation → `o2`, recalculer. Une
 *      correction suffit pour tous les cas réels à transition unique.
 *
 * Heures inexistantes (spring-forward) / ambiguës (fall-back) : on retourne le
 * candidat `o2` (offset post-transition), déterministe — suffisant pour des
 * shifts (au pire décalé du delta DST sur la seule minute concernée).
 */
export function zonedWallTimeToUtc(wall: WallClock, tz: string): number {
  const naiveUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second ?? 0,
  );
  const o1 = zoneOffsetMinutes(naiveUtc, tz);
  let candidate = naiveUtc - o1 * 60000;
  const o2 = zoneOffsetMinutes(candidate, tz);
  if (o2 !== o1) {
    candidate = naiveUtc - o2 * 60000;
  }
  return candidate;
}

/**
 * Interprète les composants LOCAUX d'un objet `Date` (an/mois/jour/h/min) comme
 * un wall-clock dans `tz` → epoch ms. Pour les pickers qui transportent l'heure
 * voulue dans un Date local (ex. `DateTimePicker`) : au submit on réinterprète
 * ces nombres dans le fuseau choisi au lieu du fuseau du navigateur.
 */
export function localDateToZonedUtc(d: Date, tz: string): number {
  return zonedWallTimeToUtc(
    {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      hour: d.getHours(),
      minute: d.getMinutes(),
      second: d.getSeconds(),
    },
    tz,
  );
}

/**
 * "YYYY-MM-DDTHH:MM" (valeur d'un `<input type="datetime-local">`) interprété
 * dans `tz` → epoch ms. Retourne NaN si la chaîne est invalide.
 */
export function datetimeLocalToZonedUtc(value: string, tz: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return NaN;
  return zonedWallTimeToUtc(
    {
      year: Number(m[1]),
      month: Number(m[2]),
      day: Number(m[3]),
      hour: Number(m[4]),
      minute: Number(m[5]),
    },
    tz,
  );
}

// ─── Formatage d'un instant dans un fuseau cible ──────────────────────────────

/** "18:00" — wall-clock HH:MM de `epochMs` vu dans `tz`. */
export function formatTimeInZone(
  epochMs: number,
  tz: string,
  locale = "fr",
): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(epochMs));
}

/** "lun. 23 juin" — date localisée de `epochMs` vue dans `tz`. */
export function formatDateInZone(
  epochMs: number,
  tz: string,
  locale = "fr",
): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(epochMs));
}

// ─── Lectures dérivées (jour de semaine / minute du jour / date calendaire) ───

const EN_WEEKDAY_TO_NUM: Record<string, number> = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7,
};

const weekdayFormatterCache = new Map<string, Intl.DateTimeFormat>();

function weekdayFormatter(tz: string): Intl.DateTimeFormat {
  let fmt = weekdayFormatterCache.get(tz);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "long" });
    weekdayFormatterCache.set(tz, fmt);
  }
  return fmt;
}

/** Jour de semaine 1=Lun … 7=Dim de `epochMs` vu dans `tz`. */
export function weekdayInZone(epochMs: number, tz: string): number {
  const wd = weekdayFormatter(tz).format(new Date(epochMs));
  return EN_WEEKDAY_TO_NUM[wd] ?? 1;
}

/** Minute du jour (0..1439) de `epochMs` vue dans `tz`. */
export function minuteOfDayInZone(epochMs: number, tz: string): number {
  const p = partsInZone(epochMs, tz);
  return p.hour * 60 + p.minute;
}

/** Date calendaire {year,month,day} de `epochMs` vue dans `tz`. */
export function calendarDateInZone(
  epochMs: number,
  tz: string,
): { year: number; month: number; day: number } {
  const p = partsInZone(epochMs, tz);
  return { year: p.year, month: p.month, day: p.day };
}

/**
 * Date calendaire (dans `tz`) à `dayOffset` jours de la date contenant
 * `anchorMs`. On ancre sur la date murale de `anchorMs` dans `tz`, puis on
 * avance par jours entiers via une sonde à MIDI UTC (jamais dans un gap DST).
 */
export function zonedDatePlusDays(
  anchorMs: number,
  tz: string,
  dayOffset: number,
): { year: number; month: number; day: number } {
  const base = calendarDateInZone(anchorMs, tz);
  const probe =
    Date.UTC(base.year, base.month - 1, base.day, 12, 0, 0) + dayOffset * DAY_MS;
  return calendarDateInZone(probe, tz);
}

/** Différence en jours entiers entre deux dates calendaires {year,month,day}. */
function dayDiff(
  a: { year: number; month: number; day: number },
  b: { year: number; month: number; day: number },
): number {
  const am = Date.UTC(a.year, a.month - 1, a.day);
  const bm = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((bm - am) / DAY_MS);
}

// ─── Conversion d'un créneau récurrent d'un fuseau vers un autre ──────────────

export interface ConvertedSlot {
  /** Jour de semaine cible (1=Lun..7=Dim) où tombe le DÉBUT. */
  startWeekday: number;
  /** Minute du jour cible du début (0..1439). */
  startMinute: number;
  /** Minute du jour cible de la fin (0..1439). */
  endMinute: number;
  /** Décalage de jour de la FIN par rapport au jour du DÉBUT (0 ou +1). */
  endDayOffset: number;
  /** Décalage de jour du DÉBUT par rapport au `weekday` source dans la zone
   *  cible (−1, 0, ou +1) — pour un indice « affiché un autre jour ». */
  startDayShift: number;
}

/**
 * Convertit UNE occurrence (un jour de semaine) d'un bloc récurrent de son
 * fuseau d'ancrage vers un fuseau d'affichage, via une occurrence concrète à
 * venir (DST correct). Différents jours d'un même bloc peuvent se décaler
 * différemment autour d'une bascule DST → on calcule PAR jour.
 *
 * @param weekday     un jour du bloc (1..7), exprimé dans le fuseau d'ancrage
 * @param startMinute bloc.startMinute (wall-clock d'ancrage)
 * @param endMinute   bloc.endMinute   (wall-clock d'ancrage)
 * @param anchorTz    bloc.timezone ?? targetTz (legacy → pas de décalage)
 * @param targetTz    fuseau d'affichage du viewer
 * @param referenceMs "maintenant" — on prend la prochaine occurrence ≥ ceci
 */
export function convertSlotToZone(
  weekday: number,
  startMinute: number,
  endMinute: number,
  anchorTz: string,
  targetTz: string,
  referenceMs: number,
): ConvertedSlot {
  // 1. Instant concret du DÉBUT : la prochaine occurrence de `weekday` dans
  //    anchorTz à startMinute.
  const anchorWd = weekdayInZone(referenceMs, anchorTz);
  const dayOffset = (weekday - anchorWd + 7) % 7; // 0..6, prochaine occurrence
  const date = zonedDatePlusDays(referenceMs, anchorTz, dayOffset);
  const startMs = zonedWallTimeToUtc(
    {
      year: date.year,
      month: date.month,
      day: date.day,
      hour: Math.floor(startMinute / 60),
      minute: startMinute % 60,
    },
    anchorTz,
  );

  const overnight = endMinute <= startMinute;
  const endDate = overnight
    ? zonedDatePlusDays(referenceMs, anchorTz, dayOffset + 1)
    : date;
  const endMs = zonedWallTimeToUtc(
    {
      year: endDate.year,
      month: endDate.month,
      day: endDate.day,
      hour: Math.floor(endMinute / 60),
      minute: endMinute % 60,
    },
    anchorTz,
  );

  // 2. Relire les deux bornes dans la zone CIBLE.
  const startWeekday = weekdayInZone(startMs, targetTz);
  const tStartMin = minuteOfDayInZone(startMs, targetTz);
  const tEndMin = minuteOfDayInZone(endMs, targetTz);

  // 3. Décalages de jour via dates calendaires cibles.
  const startDate = calendarDateInZone(startMs, targetTz);
  const endDateTgt = calendarDateInZone(endMs, targetTz);
  const endDayOffset = dayDiff(startDate, endDateTgt);

  // startDayShift : le DÉBUT est-il tombé sur un autre jour que `weekday` ?
  let startDayShift = startWeekday - weekday;
  if (startDayShift > 3) startDayShift -= 7;
  if (startDayShift < -3) startDayShift += 7;

  return {
    startWeekday,
    startMinute: tStartMin,
    endMinute: tEndMin,
    endDayOffset,
    startDayShift,
  };
}

// ─── Liste curatée pour les pickers ───────────────────────────────────────────

export interface TimezoneOption {
  /** Id IANA, ex. "Europe/Paris". */
  id: string;
  /** Libellé humain, ex. "Paris". */
  label: string;
  /** Regroupement pour <optgroup>, ex. "Europe". */
  region: string;
}

/**
 * Jeu curaté couvrant les marchés servis. On NE liste PAS les 400+ zones de
 * `Intl.supportedValuesOf('timeZone')` (picker inutilisable) — les pickers
 * préfixent la zone du device si elle manque ici.
 */
export const COMMON_TIMEZONES: TimezoneOption[] = [
  { id: "Europe/Paris", label: "Paris", region: "Europe" },
  { id: "Europe/London", label: "Londres", region: "Europe" },
  { id: "Europe/Madrid", label: "Madrid", region: "Europe" },
  { id: "Europe/Berlin", label: "Berlin", region: "Europe" },
  { id: "Europe/Rome", label: "Rome", region: "Europe" },
  { id: "Europe/Lisbon", label: "Lisbonne", region: "Europe" },
  { id: "Europe/Bucharest", label: "Bucarest", region: "Europe" },
  { id: "Europe/Athens", label: "Athènes", region: "Europe" },
  { id: "Europe/Moscow", label: "Moscou", region: "Europe" },
  { id: "America/New_York", label: "New York", region: "Amériques" },
  { id: "America/Chicago", label: "Chicago", region: "Amériques" },
  { id: "America/Denver", label: "Denver", region: "Amériques" },
  { id: "America/Los_Angeles", label: "Los Angeles", region: "Amériques" },
  { id: "America/Toronto", label: "Toronto", region: "Amériques" },
  { id: "America/Mexico_City", label: "Mexico", region: "Amériques" },
  { id: "America/Bogota", label: "Bogotá", region: "Amériques" },
  { id: "America/Sao_Paulo", label: "São Paulo", region: "Amériques" },
  { id: "America/Argentina/Buenos_Aires", label: "Buenos Aires", region: "Amériques" },
  { id: "Africa/Casablanca", label: "Casablanca", region: "Afrique" },
  { id: "Africa/Lagos", label: "Lagos", region: "Afrique" },
  { id: "Africa/Johannesburg", label: "Johannesburg", region: "Afrique" },
  { id: "Asia/Dubai", label: "Dubaï", region: "Asie" },
  { id: "Asia/Istanbul", label: "Istanbul", region: "Asie" },
  { id: "Asia/Kolkata", label: "Calcutta", region: "Asie" },
  { id: "Asia/Bangkok", label: "Bangkok", region: "Asie" },
  { id: "Asia/Singapore", label: "Singapour", region: "Asie" },
  { id: "Asia/Manila", label: "Manille", region: "Asie" },
  { id: "Asia/Shanghai", label: "Shanghai", region: "Asie" },
  { id: "Asia/Tokyo", label: "Tokyo", region: "Asie" },
  { id: "Australia/Sydney", label: "Sydney", region: "Océanie" },
  { id: "Pacific/Auckland", label: "Auckland", region: "Océanie" },
  { id: "UTC", label: "UTC", region: "Autre" },
];

/** Suffixe d'offset courant d'une zone, ex. "UTC+02:00", pour les libellés. */
export function formatOffsetLabel(tz: string, atMs?: number): string {
  const at = atMs ?? Date.now();
  const off = zoneOffsetMinutes(at, tz); // minutes, peut être négatif
  const sign = off >= 0 ? "+" : "-";
  const abs = Math.abs(off);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
}

/**
 * Nom court d'une zone pour l'écrire À CÔTÉ d'une heure — « Paris », « UTC ».
 *
 * 🩸 Une heure sans son fuseau est un piège quand tout le monde n'est pas dans
 * le même. Le 08 au 10-09-2026, le titulaire du Shift A a démarré trois jours
 * de suite à 08:30:0x **UTC** — à la seconde — c'est-à-dire 10:30 Paris : son
 * poste affiche UTC, Discord annonce « Shift A (08h30–12h30) », et rien à
 * l'écran ne disait dans quel fuseau était le nombre qu'il lisait. Deux heures
 * perdues sur quatre, chaque matin, sans une seule erreur de sa part.
 *
 * Repli sur le dernier segment IANA plutôt que sur l'id entier : « Antananarivo »
 * se lit, « Indian/Antananarivo » encombre la ligne.
 */
export function libelleDuFuseau(tz: string): string {
  const connu = COMMON_TIMEZONES.find((z) => z.id === tz);
  if (connu) return connu.label;
  const dernier = tz.split("/").pop() ?? tz;
  return dernier.replace(/_/g, " ");
}
